import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  PlayArrow as ContinueIcon,
  Stop as StopIcon,
  Celebration as CelebrationIcon
} from '@mui/icons-material';

/**
 * CheckpointView Component
 * Shows built steps and asks user to continue or stop
 */
const CheckpointView = ({ 
  checkpoint, 
  planId,
  onContinue, 
  onStop, 
  loading 
}) => {
  const { message, hasMore, progress, builtSteps } = checkpoint;
  const progressPercent = (progress.completed / progress.total) * 100;

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Progress Bar */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Build Progress
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
            {progress.completed} / {progress.total} Steps
          </Typography>
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={progressPercent} 
          sx={{ 
            height: 10, 
            borderRadius: 5,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: hasMore ? '#1976d2' : '#4caf50'
            }
          }}
        />
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {Math.round(progressPercent)}% Complete
        </Typography>
      </Paper>

      {/* Built Steps Summary */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 2 }}>
          ✅ Recently Built Steps
        </Typography>

        <List>
          {builtSteps && builtSteps.map((step, index) => (
            <React.Fragment key={index}>
              <ListItem sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <CheckIcon sx={{ color: '#4caf50' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {step.name}
                      </Typography>
                      <Chip 
                        label={step.type}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
              </ListItem>
              {index < builtSteps.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Action Section */}
      {hasMore ? (
        <Paper elevation={2} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              📋 <strong>{progress.total - progress.completed} steps</strong> remaining to build.
            </Typography>
          </Alert>

          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            ❓ Shall I continue building the next batch?
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={loading ? null : <ContinueIcon />}
              onClick={onContinue}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? 'Building...' : 'Yes, Continue'}
            </Button>
            
            <Button
              variant="outlined"
              color="warning"
              size="large"
              startIcon={<StopIcon />}
              onClick={onStop}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              Stop Here
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            💡 I'll build the next batch of steps and check with you again.
          </Typography>
        </Paper>
      ) : (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            backgroundColor: '#e8f5e9',
            textAlign: 'center'
          }}
        >
          <CelebrationIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
          
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#2e7d32' }}>
            🎉 All Steps Completed!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your flow has been successfully built with all {progress.total} steps.
          </Typography>

          <Alert severity="success">
            <Typography variant="body2">
              The flow has been saved and is ready to use. You can now test it or make further adjustments.
            </Typography>
          </Alert>
        </Paper>
      )}
    </Box>
  );
};

export default CheckpointView;

